import { ReactNode, useId, useRef } from "react";
import clsx from "clsx";
import styles from "./ChipGroup.module.scss";
import { FormattedMessage } from "react-intl";

interface ChipOption<T> {
  label: ReactNode;
  value: T;
}

interface ChipGroupBaseProps<T> {
  options: ChipOption<T>[];
  name: string;
  // note "Clear All" is optional
  onClear?: () => void;
  legend: ReactNode;
  legendClassName?: string;
  clearButtonPosition?: "start" | "end";
}

type ChipGroupProps<T> =
  | (ChipGroupBaseProps<T> & {
      type: "exclusive";
      value: T;
      onChange: (value: T) => void;
    })
  | (ChipGroupBaseProps<T> & {
      type: "multi";
      value: T[];
      onChange: (value: T[]) => void;
    });

export default function ChipGroup<T extends string>({
  type,
  options,
  name,
  value,
  onChange,
  onClear,
  legend,
  legendClassName,
  clearButtonPosition = "start",
}: ChipGroupProps<T>) {
  const ref = useRef<HTMLInputElement>(null);
  const id = useId();
  const makeInputId = (val: string) => `${id}-${val.replace(" ", "-")}`;
  const isExclusive = type === "exclusive";
  const hasSelection = isExclusive
    ? !!options.find((o) => o.value === value)
    : value.length > 0;

  const clearButton = hasSelection && onClear && (
    <button
      className={clsx("vst-link", styles.clearBtn)}
      onClick={() => {
        // clear btn is about to hide so move focus to known position
        ref.current?.focus();
        onClear();
      }}
    >
      <FormattedMessage defaultMessage="Clear All" />
    </button>
  );

  return (
    <fieldset className={"vst-chip-group"}>
      {legend && <legend className={legendClassName}>{legend}</legend>}
      {clearButtonPosition === "start" && clearButton}
      {options.map((o, i) => (
        <div key={o.value}>
          <input
            ref={i === 0 ? ref : undefined}
            type={isExclusive ? "radio" : "checkbox"}
            name={isExclusive ? name : o.value}
            value={isExclusive ? o.value : undefined}
            checked={isExclusive ? value === o.value : value.includes(o.value)}
            id={makeInputId(o.value)}
            onChange={(e) => {
              if (type === "exclusive") {
                onChange(o.value);
              } else {
                let newVals = [...value];
                if (e.target.checked && !newVals.includes(e.target.name as T)) {
                  newVals.push(e.target.name as T);
                } else {
                  newVals = newVals.filter((v) => v !== o.value);
                }
                newVals.sort(
                  (a, b) =>
                    options.findIndex((o) => o.value === a) -
                    options.findIndex((o) => o.value === b)
                );
                onChange(newVals);
              }
            }}
          />
          <label htmlFor={makeInputId(o.value)}>{o.label}</label>
        </div>
      ))}
      {clearButtonPosition === "end" && clearButton}
    </fieldset>
  );
}
